/**
 * Cliente HTTP para gestión de usuarios (Fase 9 / web v2.1.0).
 *
 * Web portal expone únicamente la vista *tenant-admin*: lista, crea, actualiza y
 * desactiva usuarios del propio `tenantId` (`auth.tenantId`). La API rechaza
 * (403) cualquier intento de asignar roles plataforma (`superadmin`,
 * `platform-support`) desde un rol no plataforma, así que la UI no expone esas
 * opciones, pero el enforcement real lo hace el backend.
 */
import { apiRequest } from "./api-client.js";

export type TenantUserRole =
  | "tenant-admin"
  | "warehouse-supervisor"
  | "warehouse-operator"
  | "finance"
  | "customer-portal-user"
  | "auditor";

export interface TenantUser {
  id: string;
  email: string;
  role: TenantUserRole | string;
  tenantId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantUserCreateInput {
  email: string;
  password: string;
  role: TenantUserRole;
}

export interface TenantUserUpdateInput {
  email?: string;
  password?: string;
  role?: TenantUserRole;
  isActive?: boolean;
}

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") q.set(k, v);
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

export async function listTenantUsers(
  token: string,
  params: { tenantId?: string; role?: string; email?: string } = {},
): Promise<{ users: TenantUser[] }> {
  return apiRequest<{ users: TenantUser[] }>(withQuery("/api/v1/users", params), { method: "GET", token });
}

export async function fetchTenantUserById(token: string, userId: string): Promise<{ user: TenantUser }> {
  return apiRequest<{ user: TenantUser }>(`/api/v1/users/${userId}`, { method: "GET", token });
}

export async function createTenantUserRequest(
  token: string,
  body: TenantUserCreateInput,
): Promise<{ user: TenantUser }> {
  return apiRequest<{ user: TenantUser }>("/api/v1/users", { method: "POST", token, body });
}

export async function updateTenantUserRequest(
  token: string,
  userId: string,
  body: TenantUserUpdateInput,
): Promise<{ user: TenantUser }> {
  return apiRequest<{ user: TenantUser }>(`/api/v1/users/${userId}`, { method: "PATCH", token, body });
}

export async function deleteTenantUserRequest(token: string, userId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/users/${userId}`, { method: "DELETE", token });
}
